/**
 * Search match-context helpers shared by every tool list:
 * AssetToolList, DisposalToolList, LifecycleToolList, ToolListScreen.
 *
 * computeMatchContext(tool, searchTerm)
 *   Returns { label, value } for the first field on `tool` whose value
 *   contains the (case-insensitive) search term, or null if no match.
 *   Property lookups fall back across camelCase and snake_case so callers
 *   can pass either a transformed or raw tool record.
 *
 * renderHighlightedValue(value, searchTerm)
 *   Returns a React fragment with the matching substring wrapped in a
 *   <span> styled via design tokens. Long values are truncated around
 *   the match with ellipses.
 */

import React from 'react';
import { getFieldDisplayLabel } from '../searchFields';

const HIGHLIGHT_STYLE = {
  color: 'var(--color-danger)',
  fontWeight: 'var(--font-semibold)',
};

export function computeMatchContext(tool, searchTerm) {
  if (!searchTerm || searchTerm.trim().length < 2) return null;
  const q = searchTerm.trim().toLowerCase();

  // 1. Backend annotation first. search_inventory tags asset-tag /
  //    part-number hits with _match_context but drops the
  //    part_numbers[]/asset_tags[] arrays, so the array checks below can't
  //    see them. Loaded-list endpoints leave _match_context null/absent, and
  //    transformToolForInventory (ToolListScreen) drops it — so this clause
  //    is inert on every non-search path.
  const mc = tool._match_context;
  if (mc && mc.field && mc.value != null && String(mc.value).trim() !== '') {
    // Session E: label resolves via the single source of label truth
    // (FIELD_DISPLAY_LABELS), covering all canonical fields — not just
    // asset_tag / part_number. Unmapped fields fall back to the raw name.
    return { label: getFieldDisplayLabel(mc.field), value: String(mc.value) };
  }

  // 2. Client-side field/array inspection (mobile, sub-min fallbacks).
  const checks = [
    ['Tool ID',     tool.toolId || tool.supplier_tool_id || tool.tool_id || ''],
    ['Alt Tool ID', tool.altToolId || tool.altId || tool.alt_tool_id || ''],
    ['Part Number', (Array.isArray(tool.part_numbers) && tool.part_numbers.find(pn => (pn || '').toLowerCase().includes(q))) || ''],
    ['Asset Tag',   (Array.isArray(tool.asset_tags) && tool.asset_tags.find(at => (at || '').toLowerCase().includes(q))) || ''],
    ['Location',    tool.siteLocation || tool.site_location || ''],
    ['Location',    tool.detailLocation || tool.detail_location || ''],
  ];

  for (const [label, value] of checks) {
    if (value && value.toLowerCase().includes(q)) return { label, value };
  }
  return null;
}

export function renderHighlightedValue(value, searchTerm) {
  const q = searchTerm.trim().toLowerCase();
  const idx = value.toLowerCase().indexOf(q);
  if (idx === -1) return value;

  let display = value;
  let matchStart = idx;

  // Truncate long values, keeping the match visible
  if (display.length > 30) {
    const before = Math.max(0, idx - 15);
    const after = Math.min(display.length, idx + q.length + 15);
    display = (before > 0 ? '…' : '') + display.slice(before, after) + (after < value.length ? '…' : '');
    matchStart = idx - before + (before > 0 ? 1 : 0);
  }

  const pre = display.slice(0, matchStart);
  const match = display.slice(matchStart, matchStart + q.length);
  const post = display.slice(matchStart + q.length);
  return <>{pre}<span style={HIGHLIGHT_STYLE}>{match}</span>{post}</>;
}
